import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../Modal';
import { useDispatch, useSelector } from 'react-redux';
import { getEnableTwoFactorAuthMethods, getDisableTwoFactorAuthMethods } from '@/redux/actions/twoFactorAuth';
import {
  IconEmailV2,
  IconMessage,
  Alert,
  IconWhatsApp,
} from 'gatorcomponents';

import * as Styles from './ChooseMethodModal.styles';
import { CardButton } from './CardButton';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { EXPERIENCIA_2FA_SMS_METHOD, EXPERIENCIA_2FA_WHATSAPP_METHOD } from '@/config/GrowthBook/constants';

const ChooseMethodModal = ({
  isOpen,
  toggleValidationCodeFlow,
  goToValidateStep,
  userEmail,
  userPhone,
  twoFactorIsEnabled,
  twoFactorAuthLocale,
}) => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const dispatch = useDispatch();
  const { loadingCodeGeneration, loadingMethods, methodsData } = useSelector(state => state.twoFactorAuth);
  const testId = 'choose-method-modal';

  const modalType = twoFactorIsEnabled ? 'disable' : 'enable';

  const handleCardClick = (type, event) => {
    event.preventDefault();
    setSelectedMethod(type);
  };

  const onSubmit = () => {
    goToValidateStep(selectedMethod);
  };

  const getMethodData = type => methodsData.find(methodData => methodData.name === type);

  const methodsList = [
    {
      id: 1,
      type: 'email',
      icon: color => <IconEmailV2 size="md" color={color} />,
      description: twoFactorAuthLocale.chooseMethodModal.email,
      value: userEmail,
      show: true,
    },
    {
      id: 2,
      type: 'sms',
      icon: color => <IconMessage size="md" color={color} />,
      description: twoFactorAuthLocale.chooseMethodModal.sms,
      value: userPhone,
      show: useFeatureIsOn(EXPERIENCIA_2FA_SMS_METHOD),
    },
    {
      id: 3,
      type: 'whatsapp',
      icon: color => <IconWhatsApp size="md" color={color} />,
      description: twoFactorAuthLocale.chooseMethodModal.whatsapp,
      value: userPhone,
      show: useFeatureIsOn(EXPERIENCIA_2FA_WHATSAPP_METHOD),
    },
  ];

  const handleMethodsRequest = useCallback(() => {
    if (isOpen) {
      twoFactorIsEnabled
        ? dispatch(getDisableTwoFactorAuthMethods())
        : dispatch(getEnableTwoFactorAuthMethods());
    }
  }, [isOpen, twoFactorIsEnabled, dispatch]);

  useEffect(() => {
    handleMethodsRequest();
  }, [handleMethodsRequest]);

  return (
    <Modal
      open={isOpen}
      title={twoFactorAuthLocale.chooseMethodModal.title(modalType)}
      onClose={toggleValidationCodeFlow}
      primaryButtonAction={onSubmit}
      primaryButtonLabel={twoFactorAuthLocale.chooseMethodModal.primaryButtonLabel}
      secondaryButtonAction={toggleValidationCodeFlow}
      secondaryButtonLabel={twoFactorAuthLocale.chooseMethodModal.secondaryButtonLabel}
      isDisabled={!selectedMethod}
      isLoading={loadingCodeGeneration}
      testId={testId}
    >
      <Styles.Container>
        <Styles.Description data-testid={`${testId}__description`}>
          {twoFactorAuthLocale.chooseMethodModal.description}
        </Styles.Description>
        <Styles.MethodsWrapper>
          {methodsList.map(({
            id,
            type,
            icon,
            description,
            value,
            show,
          }) => (
            <CardButton
              key={id}
              show={show}
              description={description}
              value={value}
              type={type}
              isSelected={type === selectedMethod}
              onClick={handleCardClick}
              twoFactorAuthLocale={twoFactorAuthLocale}
              methodData={getMethodData(type)}
              isLoadingMethods={loadingMethods}
              icon={icon}
              testId={`choose-method-modal__card-button-${type}`}
            />
          ))}
        </Styles.MethodsWrapper>
      </Styles.Container>

      {twoFactorIsEnabled && (
        <Styles.AlertWrapper>
          <Alert
            variant="error"
            title={twoFactorAuthLocale.validateCodeModal.disable2FA.warning.title}
            description={twoFactorAuthLocale.validateCodeModal.disable2FA.warning.description}
            testId={`${testId}__alert`}
          />
        </Styles.AlertWrapper>
      )}

      <Styles.Divider />
    </Modal>
  );
};

export default ChooseMethodModal;
